import type { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import type { ClimateOption, FuelOption, InputTypeOption, Option, SalinityOption } from "../../../types/modulesInterfaces";
import { BEModulesClassNames } from "@utils/moduleList";
import type { TagTypes } from "@app/apiSlice";
import type { ApiSliceType, PublicApiSliceType } from "@typedefs/interfaces";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";
interface LandUseProps {
  moduleType?: number;
  climate?: number;
  moisture?: number;
}

interface GWPOption extends Option {
  id: number;
  name: string;
  co2: number;
  ch4: number;
  n2o: number;
}

interface DataSourceOption extends Option {
  short_name: string;
}


export function createDropdownOptionsApi(apiSlice: ApiSliceType | PublicApiSliceType) {
  const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
    getTypesOfFishery: builder.query<Option[], void>({
      query: () => "fishery-types/",
    }),
    getFishCategories: builder.query<Option[], void>({
      query: () => "fish-types/",
    }),
    getSmallFishGearCategory: builder.query<Option[], { fishery_types?: number }>({
      query: ({ fishery_types }) => {
        const params = new URLSearchParams();
        if (fishery_types)
          params.append('fishery_types', fishery_types.toString());
        return `small-fishery-gear-types/${params.toString() ? `?${params.toString()}` : ''}`
      }
    }),
    getLargeFishGearCategories: builder.query<Option[], void>({
      query: () => "large-fishery-gear-types/",
    }),
    getCropTypes: builder.query<Option[], void>({
      query: () => "crop-types/",
    }),
    getTillageTypes: builder.query<Option[], void>({
      query: () => "tillage-management-types/",
    }),
    getOrganicInputTypes: builder.query<Option[], void>({
      query: () => "organic-input-types/",
    }),
    getResidueTypes: builder.query<Option[], void>({
      query: () => "residue-management-types/",
    }),
    getSoilTypes: builder.query<Option[], { moduleId?: number }>({
      query: ({ moduleId }) => {
        const params = new URLSearchParams();
        const moduleRegistry = ModuleTypeRegistry.getInstance();
        const coastalWetlands = moduleRegistry.getIdByClassName(BEModulesClassNames.CoastalWetlands);
        if (moduleId && moduleId === coastalWetlands)
          params.append('is_coastal', 'true');

        return `soil-types/${params.toString() ? `?${params.toString()}` : ''}`
      },
    }),
    getClimateTypes: builder.query<ClimateOption[], void>({
      query: () => "climates/",
    }),
    getMoistureTypes: builder.query<Option[], void>({
      query: () => "moistures/",
    }),
    getGrasslandTypes: builder.query<Option[], void>({
      query: () => "grassland-management-types/",
    }),
    getProjectStatuses: builder.query<Option[], void>({
      query: () => "statuses/",
    }),
    getCountries: builder.query<Option[], void>({
      query: () => "countries/",
      transformResponse: (response: Option[]) => response.sort((a, b) => a.name.localeCompare(b.name)),
    }),
    getGWPSources: builder.query<GWPOption[], void>({
      query: () => "global-warming-potentials/",
    }),
    getLandUseTypes: builder.query<Option[], LandUseProps>({
      query: ({ moduleType, climate, moisture }) => {
        const params = new URLSearchParams();
        if (moduleType) params.append('module_type', moduleType.toString());
        if (climate) params.append('climate', climate.toString());
        if (moisture) params.append('moisture', moisture.toString());
        console.log("get land use types params", params.toString());
        return `land-use-types/${params.toString() ? `?${params.toString()}` : ''}`
      }
    }),
    getLivestockCategories: builder.query<Option[], void>({
      query: () => "livestock-category-types/",
    }),
    getLivestockProductionTypes: builder.query<Option[], void>({
      query: () => "livestock-production-types/",
    }),
    getManureManagementTypes: builder.query<Option[], void>({
      query: () => "manure-management-types/",
    }),
    getWaterManagementTypesBC: builder.query<Option[], void>({
      query: () => "water-management-types-before-cultivation/",
    }),
    getWaterManagementTypesAC: builder.query<Option[], void>({
      query: () => "water-management-types-after-cultivation/",
    }),
    getOrganicAmendmentTypes: builder.query<Option[], void>({
      query: () => "organic-amendment-types/",
    }),
    getDisturbanceTypes: builder.query<Option[], void>({
      query: () => "disturbance-types/",
    }),
    getForestTypes: builder.query<Option[], void>({
      query: () => "forest-types/",
    }),
    getForestConditionTypes: builder.query<Option[], void>({
      query: () => "forest-condition-types/",
    }),
    getFundingAgencies: builder.query<Option[], void>({
      query: () => "funding-agencies/",
      providesTags: ["FundingAgencies"],
    }),
    getWaterbodyTypes: builder.query<Option[], void>({
      query: () => "waterbody-types/",
    }),
    getTrophicTypes: builder.query<Option[], void>({
      query: () => "trophic-types/",
    }),
    getFuelTypes: builder.query<FuelOption[], { search?: string[], module_types?: number }>({
      query: ({ search, module_types }) => {
        const params = new URLSearchParams();
        if (search)
          search.forEach(term => params.append('s', term));
        if (module_types)
          params.append('module_types', module_types.toString());
        return {
          url: `fuel-types/${params.toString() ? `?${params.toString()}` : ''}`,
          headers: { 'Accept-Language': "en" } // FIXED THE LANGUAGE TO AVOID SEARCH FILTERING BUG
        }
      },
      transformResponse: (response: FuelOption[]) => response.map((fuelOption) => ({
        ...fuelOption,
        name: `${fuelOption.name} - ${fuelOption.fuel_use_type.name}`,
        name_en: `${fuelOption.name_en} - ${fuelOption.fuel_use_type.name_en}`,
        name_fr: `${fuelOption.name_fr ?? fuelOption.name_en} - ${fuelOption.fuel_use_type.name_fr ?? fuelOption.fuel_use_type.name_en}`,
        name_es: `${fuelOption.name_es ?? fuelOption.name_en} - ${fuelOption.fuel_use_type.name_es ?? fuelOption.fuel_use_type.name_en}`,
        name_ru: `${fuelOption.name_ru ?? fuelOption.name_en} - ${fuelOption.fuel_use_type.name_ru ?? fuelOption.fuel_use_type.name_en}`,
      })),
    }),
    getSalinityTypes: builder.query<Option[], void>({
      query: () => 'salinity-types/',
      transformResponse: (response: SalinityOption[]) => response.map(({ id, value }) => ({ id, name: value }))
    }),
    getMacroFuelTypes: builder.query<Option[], void>({
      query: () => "macro-fuel-types/",
    }),
    getPeatTypes: builder.query<Option[], void>({
      query: () => "peat-types/",
    }),
    getFireTypes: builder.query<Option[], void>({
      query: () => "fire-types/",
    }),
    getIrrigationSystemTypes: builder.query<Option[], { moduleTypes?: number }>({
      query: (params) => ({
        url: 'irrigation-system-types/',
        params: {
          module_types: params?.moduleTypes
        }
      })
    }),
    createFundingAgency: builder.mutation<Option, string>({
      query: (name) => ({
        url: "funding-agencies/",
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["FundingAgencies"],
    }),
    getExecutingAgencies: builder.query<Option[], void>({
      query: () => "executing-agencies/",
      providesTags: ["ExecutingAgencies"],
    }),
    createExecutingAgency: builder.mutation<Option, string>({
      query: (name) => ({
        url: "executing-agencies/",
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["ExecutingAgencies"],
    }),
    getRoadTypes: builder.query<Option[], void>({
      query: () => 'road-types/'
    }),
    getBuildingTypes: builder.query<Option[], void>({
      query: () => 'building-types/'
    }),
    getEmissionFactorSources: builder.query<Option[], void>({
      query: () => 'emission-factor-sources/'
    }),
    getSettlementTypes: builder.query<Option[], void>({
      query: () => 'settlement-types/'
    }),
    getChangeRates: builder.query<Option[], void>({
      query: () => 'change-rates/'
    }),
    getRefrigerantTypes: builder.query<Option[], void>({
      query: () => 'refrigerant-types/'
    }),
    getPackagingMaterialTypes: builder.query<Option[], void>({
      query: () => 'packaging-material-types/'
    }),
    getInputTypes: builder.query<InputTypeOption[], void>({
      query: () => "input-types/",
    }),
    getDataSourceTypes: builder.query<Option[], number | undefined>({
      query: (moduleType) => `data-sources/${moduleType ? `?module_type=${moduleType}` : ''}`,
      transformResponse: (response: DataSourceOption[]) => response.map((opt) => ({ ...opt, name: opt.short_name }))
    }),
  });

  return apiSlice.injectEndpoints({ endpoints });
} 